import React, { useState, useEffect, useMemo, useRef } from 'react';
import { PersonFinal } from '@/api/entities';
import { PersonaActivity } from '@/api/entities';
import { AccountFinal } from '@/api/entities';
import { PersonaConnection, ExternalConnection } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { User as UserIcon, Mail, ChevronRight, Search, Loader2, CheckCircle, ArrowUp, ArrowDown, ChevronUp, ChevronDown, Filter, Linkedin, Check, GraduationCap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PopoverStaysOpen, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { format, isValid } from 'date-fns';
import { applyTenantOverrides } from '@/components/utils/tenantOverrides';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import PlaybookMarker from '../components/PlaybookMarker';
import PursuitMarker from '../components/PursuitMarker';
import { PlaybookStatusProvider } from '@/components/PlaybookStatusContext';
import { useTranslation } from '@/components/utils/translations';
import PersonAvatar from '@/components/PersonAvatar';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { buildTenantFilter } from '@/components/utils/accountResolver';
import { getLearnwiseEventDisplayLabel } from '@/lib/learnwiseEventKeywords';
import { personaSharesAlmaMater } from '@/lib/almaMaterMatch';
import { fetchUserUniversities } from '@/lib/userUniversities';
import { indexAccountsForPersonaJoin, resolveAccountForPersona } from '@/lib/accountPoolJoin';
import { personaFitBand } from '@/lib/personaFitLabel';

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';

// Enhanced retry function with better rate limit handling
const retryWithBackoff = async (fn, maxAttempts = 3, baseDelay = 2000) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            return await fn();
        } catch (error) {
            const isRateLimit = error.response?.status === 429 || error.status === 429;
            
            if (isRateLimit && attempt < maxAttempts) {
                const delay = baseDelay * Math.pow(2, attempt - 1);
                console.warn(`⏳ Rate limit hit (attempt ${attempt}/${maxAttempts}), waiting ${delay}ms before retrying...`, error);
                await new Promise(resolve => setTimeout(resolve, delay));
            } else {
                throw error;
            }
        }
    }
};

const PERSONA_COLORS = {
    strategic: "bg-green-100 text-green-800",
    supportive: "bg-blue-100 text-blue-800",
    passive: "bg-gray-100 text-gray-800",
    lowFit: "bg-red-100 text-red-800",
};
const getPersonaLabel = (score, t) => {
    const band = personaFitBand(score) ?? 'passive';
    return { label: t(band), color: PERSONA_COLORS[band] };
};

// ENHANCED: Normalize account names with better handling for hospital naming quirks
const normalizeAccountName = (name) => {
  if (!name) return '';
  return name
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove accents/diacritics
    .replace(/\(.*?\)/g, ' ')                          // drop bracketed tokens like (HCL)
    .replace(/&/g, ' and ')
    .replace(/[^\w\s]/g, ' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

const sanitizeId = (id) => {
  if (typeof id === 'string' || typeof id === 'number') {
    return String(id);
  }
  return '';
};

const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

/** Upper bound for PersonFinal.list (tenant-scale; avoids unbounded queries). */
const PEOPLE_FETCH_LIMIT = 50000;
const PERSONA_CONNECTIONS_FETCH_LIMIT = 100000;

function getPeopleCacheKey(tenantId) {
  return `people-cache-v5-${tenantId || 'default'}`;
}

function readPeopleCache(tenantId) {
  try {
    const cacheKey = getPeopleCacheKey(tenantId);
    const raw = sessionStorage.getItem(cacheKey);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.cachedAt > CACHE_DURATION) {
      sessionStorage.removeItem(cacheKey);
      return null;
    }
    return data;
  } catch { return null; }
}

function writePeopleCache(people, tenantId) {
  try {
    const cacheKey = getPeopleCacheKey(tenantId);
    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({ people, cachedAt: Date.now() })
    );
  } catch {}
}

/** Normalize events_attended for calmedica tenant: group variants (e.g. SantExpo 2024, SantExpo 2025) into one label */
function normalizeCalmedicaEvent(raw) {
  if (raw == null || raw === '') return '';
  const str = typeof raw === 'string' ? raw : String(raw);
  if (!str.trim()) return '';
  let s = str.replace(/\s*\([^)]*\)\s*$/, '').trim();
  s = s.replace(/\s+20\d{2}\s*$/, '').replace(/\s+19\d{2}\s*$/, '').trim();
  if (/^CHU HealthTech/i.test(s)) return 'CHU HealthTech';
  if (/^SantExpo/i.test(s)) return 'SantExpo';
  return s;
}

/** events_attended may be an array, JSON string, comma-separated string, or objects — align with PersonProfile toArray */
function parseEventsAttendedList(value) {
  if (!value) return [];
  const coerceItem = (item) => {
    if (item == null) return null;
    if (typeof item === 'string') {
      const t = item.trim();
      return t && t !== 'None identified' && t !== 'null' ? t : null;
    }
    if (typeof item === 'object') {
      const s = item.name ?? item.event ?? item.title ?? item.label ?? item.value;
      if (typeof s === 'string' && s.trim()) return s.trim();
      return null;
    }
    const t = String(item).trim();
    return t && t !== 'None identified' && t !== 'null' ? t : null;
  };
  if (Array.isArray(value)) {
    return value.map(coerceItem).filter(Boolean);
  }
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s || s === 'None identified' || s === 'null') return [];
    if (s.startsWith('[')) {
      try {
        return parseEventsAttendedList(JSON.parse(s));
      } catch {
        /* fall through */
      }
    }
    return s
      .split(/,(?![^(]*\))/)
      .map((x) => x.trim())
      .filter((x) => x && x !== 'None identified' && x !== 'null');
  }
  return [];
}

function eventLabelForPersonFilter(raw, tenant) {
  const str =
    typeof raw === 'string'
      ? raw.trim()
      : raw && typeof raw === 'object'
        ? String(raw.name ?? raw.event ?? raw.title ?? raw.label ?? raw.value ?? '').trim()
        : String(raw ?? '').trim();
  if (!str) return '';
  if (tenant === 'calmedica') return normalizeCalmedicaEvent(str);
  if (tenant === 'learnwise') return getLearnwiseEventDisplayLabel(str);
  return str;
}

/** Multi-select filter: any selected value matches (trim + case-insensitive). */
function multiSelectMatches(selected, fieldValue) {
  if (!selected?.length) return true;
  if (fieldValue == null || fieldValue === '') return false;
  const v = String(fieldValue).trim();
  return selected.some((s) => v.localeCompare(String(s).trim(), undefined, { sensitivity: 'base' }) === 0);
}

/** Accent-insensitive match for search and A–Z sort. */
function normalizePersonNameForMatch(name) {
  if (!name) return '';
  return String(name)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/** Best display/sort label: parts, full_name, or legacy name field. */
function getPersonDisplayName(person) {
  if (!person) return '';
  const fromParts = `${person.first_name || ''} ${person.last_name || ''}`.trim();
  return (
    (person.fullName && String(person.fullName).trim()) ||
    (person.full_name && String(person.full_name).trim()) ||
    fromParts ||
    (person.name && String(person.name).trim()) ||
    ''
  );
}

function getPersonLastNameForSort(person) {
  const last = String(person?.last_name || '').trim();
  if (last) return last;
  const full = getPersonDisplayName(person);
  if (!full) return '';
  const parts = full.split(/\s+/).filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 1] : full;
}

function getPersonFirstNameForSort(person) {
  const first = String(person?.first_name || '').trim();
  if (first) return first;
  const full = getPersonDisplayName(person);
  if (!full) return '';
  const parts = full.split(/\s+/).filter(Boolean);
  return parts.length > 1 ? parts.slice(0, -1).join(' ') : '';
}

function comparePeopleByName(a, b, direction = 'asc') {
  const aFirst = normalizePersonNameForMatch(getPersonFirstNameForSort(a));
  const bFirst = normalizePersonNameForMatch(getPersonFirstNameForSort(b));
  const aLast = normalizePersonNameForMatch(getPersonLastNameForSort(a));
  const bLast = normalizePersonNameForMatch(getPersonLastNameForSort(b));
  let cmp = aFirst.localeCompare(bFirst, undefined, { sensitivity: 'base' });
  if (cmp === 0) cmp = aLast.localeCompare(bLast, undefined, { sensitivity: 'base' });
  if (cmp === 0) {
    cmp = normalizePersonNameForMatch(getPersonDisplayName(a)).localeCompare(
      normalizePersonNameForMatch(getPersonDisplayName(b)),
      undefined,
      { sensitivity: 'base' }
    );
  }
  return direction === 'desc' ? -cmp : cmp;
}

function buildPersonFullNameField(person) {
  return getPersonDisplayName(person);
}

function personHasConnections(person) {
  if (!person || typeof person !== 'object') return false;

  const numericCandidates = [
    person.connections_count,
    person.connection_count,
    person.num_connections,
    person.linkedin_connections_count,
  ];
  for (const candidate of numericCandidates) {
    const n = Number(candidate);
    if (Number.isFinite(n) && n > 0) return true;
  }

  const booleanCandidates = [
    person.has_connections,
    person.has_connection,
    person.has_linkedin_connections,
  ];
  for (const candidate of booleanCandidates) {
    if (candidate === true) return true;
  }

  const listCandidates = [
    person.connections,
    person.connection_data,
    person.connections_data,
    person.linkedin_connections,
  ];
  for (const candidate of listCandidates) {
    if (Array.isArray(candidate) && candidate.length > 0) return true;
  }

  return false;
}

export default function People({ currentTenant: currentTenantProp }) {
    const { t } = useTranslation();
    const contextUser = useUser();
    const contextTenant = useCurrentTenant();
    const currentTenant = currentTenantProp || contextTenant;
    const [people, setPeople] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [countryFilter, setCountryFilter] = useState("all");
    const [eventFilter, setEventFilter] = useState("all");
    const [accountFilter, setAccountFilter] = useState([]);
    const [departmentFilter, setDepartmentFilter] = useState([]);
    const [archetypeFilter, setArchetypeFilter] = useState([]);
    const [formerClientFilter, setFormerClientFilter] = useState('all');
    const [connectionsFilter, setConnectionsFilter] = useState('all');
    const [pursuitFilter, setPursuitFilter] = useState('all');
    const [userUniversities, setUserUniversities] = useState([]);
    const [socialPresenceMinScore, setSocialPresenceMinScore] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'persona_fit_score', direction: 'desc' });
    const [hydratedFromCache, setHydratedFromCache] = useState(false);
    const [effectiveTenant, setEffectiveTenant] = useState(null);
    const lastLoadedTenantRef = useRef(null);

    useEffect(() => {
        if (!contextUser) return;
        const resolvedTenant = resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, currentTenant, contextUser, 'instructure');
        const cached = readPeopleCache(resolvedTenant);
        if (cached?.people) {
            console.log('📦 People: Hydrating from cache for tenant:', resolvedTenant);
            setEffectiveTenant(resolvedTenant);
            setPeople(cached.people);
            setHydratedFromCache(true);
            setIsLoading(false);
        }
    }, [currentTenant, contextUser]);

    useEffect(() => {
        if (!contextUser) return;
        const resolvedTenant = resolveEffectiveTenant(
            contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
            currentTenant,
            contextUser,
            'instructure'
        );
        const userId = contextUser.id || contextUser.user_id;
        if (!userId) return;
        let cancelled = false;
        fetchUserUniversities(resolvedTenant, userId).then((rows) => {
            if (!cancelled) setUserUniversities(rows);
        });
        return () => {
            cancelled = true;
        };
    }, [currentTenant, contextUser]);

    const almaMaterMatchByPersonId = useMemo(() => {
        const map = new Map();
        if (!userUniversities?.length) return map;
        for (const p of people) {
            const match = personaSharesAlmaMater(p, userUniversities);
            if (match) {
                const id = p.person_id || p.id;
                if (id) map.set(id, match);
            }
        }
        return map;
    }, [people, userUniversities]);

    useEffect(() => {
        const loadPeople = async () => {
            if (!contextUser) return;
            const resolvedTenant = resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, currentTenant, contextUser, 'instructure');
            setEffectiveTenant(resolvedTenant);
            const effectiveTenant = resolvedTenant;

            console.log('🔍 People Load Start:', {
                currentTenant,
                userTenantId: contextUser?.tenant_id,
                effectiveTenant,
                hydratedFromCache,
                peopleCount: people.length,
                lastLoadedTenant: lastLoadedTenantRef.current
            });

            // FORCE CLEAR CACHE ON TENANT CHANGE
            if (lastLoadedTenantRef.current && lastLoadedTenantRef.current !== effectiveTenant) {
                console.log('🗑️ Clearing cache due to tenant change');
                sessionStorage.removeItem(getPeopleCacheKey(lastLoadedTenantRef.current));
                setPeople([]);
                setHydratedFromCache(false);
            }

            if (hydratedFromCache && lastLoadedTenantRef.current === effectiveTenant && people.length > 0) {
                console.log(`People: Skipping load for tenant ${effectiveTenant} - already hydrated/loaded.`);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);

            console.log(`📊 Loading People for tenant: ${effectiveTenant}`);
            
            const tenantFilter = buildTenantFilter(effectiveTenant);
            
            const tenantMatches = (record) => {
              if (!record) return false;
              if (effectiveTenant === 'smowl') return record.tenant_id === 'smowl';
              if (record.tenant_id === effectiveTenant || record.tenant_id === 'global') return true;
              if (effectiveTenant === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id)) return true;
              if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(effectiveTenant)) return true;
              if (record.tenant_overrides && record.tenant_overrides[effectiveTenant]) return true;
              if (effectiveTenant === 'diambra' && (record.tenant_id === 'smowl' || record.tenant_id === 'instructure' || record.tenant_id === 'clairelabs' || record.tenant_id === 'base44' || record.tenant_id === 'default')) {
                return true;
              }
              if (effectiveTenant === 'edqwest') return record.tenant_id === 'edqwest';
              if (effectiveTenant === 'faria') return record.tenant_id === 'edqwest';
              if (effectiveTenant === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
              if (effectiveTenant === 'sebit') return record.tenant_id === 'sebit' || record.tenant_id === 'global';
              if (effectiveTenant === 'prowise') return record.tenant_id === 'prowise' || record.tenant_id === 'global';
              if (effectiveTenant === 'skillwell') return record.tenant_id === 'skillwell' || record.tenant_id === 'skillwise' || record.tenant_id === 'global';
              if (effectiveTenant === 'kairadigital') return record.tenant_id === 'kairadigital';
              if (effectiveTenant === 'learnwise') return record.tenant_id === 'learnwise' || record.tenant_id === 'global';
              return false;
            };

            try {
                const allPersonData = await retryWithBackoff(() =>
                  PersonFinal.filter(
                    { is_visible: { $ne: false }, tenant_id: tenantFilter },
                    '-persona_fit_score',
                    PEOPLE_FETCH_LIMIT
                  )
                );

                console.log(`📊 People: Fetched ${allPersonData.length} person records from database`);

                let pursuitIds = new Set();
                let connectedPersonIds = new Set();

                const peopleAfterTenantFilteringAndOverrides = allPersonData
                  .filter(person => tenantMatches(person))
                  .map(person => {
                    if (person.tenant_overrides && person.tenant_overrides[effectiveTenant]) {
                      return { ...person, ...person.tenant_overrides[effectiveTenant] };
                    }
                    return person;
                  });

                console.log(`📊 People: After tenant filtering: ${peopleAfterTenantFilteringAndOverrides.length} people`);

                try {
                  const [peerRows, externalRows] = await Promise.all([
                    retryWithBackoff(() =>
                      PersonaConnection.filter(
                        { tenant_id: effectiveTenant },
                        '',
                        PERSONA_CONNECTIONS_FETCH_LIMIT
                      )
                    ).catch((e) => {
                      console.warn('Could not load persona_connections:', e);
                      return [];
                    }),
                    retryWithBackoff(() =>
                      ExternalConnection.filter(
                        { tenant_id: effectiveTenant },
                        '',
                        PERSONA_CONNECTIONS_FETCH_LIMIT
                      )
                    ).catch((e) => {
                      console.warn('Could not load external_connections:', e);
                      return [];
                    }),
                  ]);

                  connectedPersonIds = new Set(
                    [
                      ...(peerRows || []).flatMap((row) => [
                        sanitizeId(row?.person_a_id),
                        sanitizeId(row?.person_b_id),
                      ]),
                      ...(externalRows || []).map((row) =>
                        sanitizeId(row?.person_id)
                      ),
                    ].filter(Boolean)
                  );

                  console.log('🔗 People: Loaded connections:', {
                    peerRows: (peerRows || []).length,
                    externalRows: (externalRows || []).length,
                    uniquePeople: connectedPersonIds.size,
                  });
                } catch (e) {
                  console.warn('Could not load connections for People page:', e);
                }

                const initialPeopleForDisplay = peopleAfterTenantFilteringAndOverrides
                  .filter(person => person.is_visible !== false)
                  .map(person => ({
                    ...person,
                    fullName: buildPersonFullNameField(person),
                    has_connections:
                      connectedPersonIds.has(sanitizeId(person.person_id)) ||
                      connectedPersonIds.has(sanitizeId(person.id)),
                  }));

                console.log(`📊 People: Initial ${initialPeopleForDisplay.length} people loaded for ${effectiveTenant}`);
                
                // Single personas-in-pursuit fetch reused for initial paint + background merge
                let peopleWithPursuit = initialPeopleForDisplay;
                try {
                  const pursuitRes = await fetch(
                    `${API_BASE_URL}/api/diy-playbooks/personas-in-pursuit?tenant_id=${effectiveTenant}&user_id=${contextUser?.id}`,
                    { credentials: 'include' }
                  );
                  if (pursuitRes.ok) {
                    const pursuitData = await pursuitRes.json();
                    const pursuitArray = pursuitData.personas || pursuitData || [];
                    pursuitIds = new Set(
                      pursuitArray
                        .map((entry) =>
                          sanitizeId(
                            typeof entry === 'object' && entry != null ? entry.person_id ?? entry.id : entry
                          )
                        )
                        .filter(Boolean)
                    );

                    peopleWithPursuit = initialPeopleForDisplay.map(p => ({
                      ...p,
                      add_to_pursuit: pursuitIds.has(p.person_id) || pursuitIds.has(p.id),
                    }));

                    console.log('✅ People page: Pursuit status:', pursuitIds.size, 'personas');
                  }
                } catch (e) {
                  console.warn('Could not load pursuit status initially:', e);
                }
                
                setPeople(peopleWithPursuit);
                
                (async () => {
                  try {
                    // Fetch accounts with reasonable limit
                    const allAccountData = await retryWithBackoff(() => AccountFinal.filter({ tenant_id: tenantFilter }, "-account_fit_score", 500)); // Reduced from 2000 to 500
                    
                    console.log(`🏢 People: Fetched ${allAccountData.length} accounts from database`);
                    
                    const accountsAfterTenantFiltering = allAccountData.filter(account => tenantMatches(account));
                    const visibleAccounts = accountsAfterTenantFiltering.filter(acc => acc.is_visible !== false);
                    
                    console.log(`🏢 People: ${visibleAccounts.length} visible accounts for tenant ${effectiveTenant}`);
                    
                    setAccounts(visibleAccounts);

                    const idIndexes = indexAccountsForPersonaJoin(visibleAccounts);
                    const accountsByName = new Map();
                    
                    visibleAccounts.forEach(acc => {
                      const names = [
                        acc.account_name,
                        acc.short_name,
                        acc.abbreviation,
                        ...(Array.isArray(acc.aliases) ? acc.aliases : [])
                      ].filter(Boolean);
                      
                      names.forEach(n => {
                        const norm = normalizeAccountName(n);
                        if (norm) {
                          accountsByName.set(norm, acc);
                        }
                      });
                    });


                    const peopleBeforeJoin = peopleAfterTenantFilteringAndOverrides
                      .filter(person => person.is_visible !== false);
                    console.log(`📊 People: pre-join ${peopleBeforeJoin.length} visible people, ${visibleAccounts.length} accounts`);

                    const finalPeople = peopleBeforeJoin
                      .map(person => {
                        const associatedAccount = resolveAccountForPersona(
                          person,
                          idIndexes,
                          accountsByName,
                          normalizeAccountName,
                        );

                        return {
                            ...person,
                            _matchedAccountId: associatedAccount
                              ? (associatedAccount.id || associatedAccount.account_id)
                              : null,
                            _accountMatch: Boolean(associatedAccount),
                            country: person.country || associatedAccount?.country,
                            fullName: buildPersonFullNameField(person),
                            add_to_pursuit: pursuitIds.has(person.person_id) || pursuitIds.has(person.id),
                            has_connections:
                              connectedPersonIds.has(sanitizeId(person.person_id)) ||
                              connectedPersonIds.has(sanitizeId(person.id)),
                        };
                    });
            
                    console.log('📊 Final people count:', finalPeople.length);
                    console.log('📊 People with account matches:', finalPeople.filter(p => p._accountMatch).length);
                    console.log('📊 People without account matches:', finalPeople.filter(p => !p._accountMatch).length);

                    setPeople(finalPeople);
                    writePeopleCache(finalPeople, effectiveTenant);
                    
                  } catch (err) {
                    console.warn('Background accounts load failed:', err);
                  }
                })();
        
            } catch (error) {
                console.error("Error loading people:", error);
                setPeople([]);
            } finally {
                setIsLoading(false);
                setHydratedFromCache(true);
                lastLoadedTenantRef.current = effectiveTenant;
            }
        };

        loadPeople();
    }, [currentTenant, contextUser]);

    const filteredPeople = people.filter(person => {
        const q = normalizePersonNameForMatch(searchTerm.trim());
        const displayName = getPersonDisplayName(person);
        const normalizedName = normalizePersonNameForMatch(displayName);
        const matchesSearch =
            !q ||
            (normalizedName && normalizedName.includes(q)) ||
            (person.role_title && normalizePersonNameForMatch(person.role_title).includes(q)) ||
            (person.account_name && normalizePersonNameForMatch(person.account_name).includes(q));
        const matchesCountry =
            countryFilter === 'all' ||
            String(person.country || '')
                .trim()
                .localeCompare(String(countryFilter).trim(), undefined, { sensitivity: 'base' }) === 0;
        const matchesEvent =
            eventFilter === 'all' ||
            (() => {
                const events = parseEventsAttendedList(person.events_attended);
                if (events.length === 0) return false;
                return events.some((ev) => eventLabelForPersonFilter(ev, effectiveTenant) === eventFilter);
            })();
        const matchesAccount = multiSelectMatches(accountFilter, person.account_name);
        const matchesDepartment = multiSelectMatches(departmentFilter, person.department);
        const matchesArchetype = multiSelectMatches(archetypeFilter, person.archetype_name);
        const matchesFormerClient =
            formerClientFilter === 'all' ||
            (formerClientFilter === 'former_client' && person.worked_for_customer);
        const hasConnections = personHasConnections(person);
        const personKey = person.person_id || person.id;
        const almaMaterMatch = personKey ? almaMaterMatchByPersonId.get(personKey) : null;
        const matchesConnections =
            connectionsFilter === 'all' ||
            (connectionsFilter === 'with_connections' && hasConnections) ||
            (connectionsFilter === 'without_connections' && !hasConnections) ||
            (connectionsFilter === 'alma_mater' && !!almaMaterMatch);
        const matchesPursuit =
            pursuitFilter === 'all' ||
            (pursuitFilter === 'in_pursuit' && !!person.add_to_pursuit) ||
            (pursuitFilter === 'not_in_pursuit' && !person.add_to_pursuit);
        const score = person.social_presence_score != null ? Number(person.social_presence_score) : null;
        const minScoreRaw = String(socialPresenceMinScore ?? '').trim();
        const parsed = parseFloat(minScoreRaw);
        const minScore = minScoreRaw === '' || isNaN(parsed) ? null : parsed;
        const matchesSocialPresence =
            minScore == null ||
            (score != null && score >= minScore);
        return matchesSearch && matchesCountry && matchesEvent && matchesAccount && matchesDepartment && matchesArchetype && matchesFormerClient && matchesConnections && matchesPursuit && matchesSocialPresence;
    });
    
    const filterFacets = useMemo(() => {
        const countries = new Set();
        const events = new Set();
        const accountsSet = new Set();
        const departments = new Set();
        const archetypes = new Set();
        const tenant = effectiveTenant;

        for (const p of people) {
            if (p.country) countries.add(p.country);
            if (p.account_name) accountsSet.add(p.account_name);
            if (p.department) departments.add(p.department);
            if (p.archetype_name) archetypes.add(p.archetype_name);
            for (const raw of parseEventsAttendedList(p.events_attended)) {
                const label = eventLabelForPersonFilter(raw, tenant);
                if (label) events.add(label);
            }
        }

        const localeSort = (a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });

        return {
            countries: [...countries].sort(),
            events: [...events].sort(),
            accounts: [...accountsSet].sort(localeSort),
            departments: [...departments].sort(localeSort),
            archetypes: [...archetypes].sort(localeSort),
        };
    }, [people, effectiveTenant]);

    const availableCountries = filterFacets.countries;
    const availableEvents = filterFacets.events;
    const availableAccounts = filterFacets.accounts;
    const availableDepartments = filterFacets.departments;
    const availableArchetypes = filterFacets.archetypes;

    const sortedPeople = useMemo(() => {
        let sortableItems = [...filteredPeople];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                if (sortConfig.key === 'first_name') {
                    return comparePeopleByName(a, b, sortConfig.direction);
                }

                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (['fullName', 'account_name', 'archetype_name', 'country', 'department'].includes(sortConfig.key)) {
                    aValue = typeof aValue === 'string' ? aValue.toLowerCase() : '';
                    bValue = typeof bValue === 'string' ? bValue.toLowerCase() : '';
                }
                
                if (aValue === null || aValue === undefined || aValue === '') return 1;
                if (bValue === null || bValue === undefined || bValue === '') return -1;
                
                let comparison = 0;
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    comparison = aValue.localeCompare(bValue, undefined, { sensitivity: 'base' });
                } else if (aValue instanceof Date && bValue instanceof Date) {
                    comparison = aValue.getTime() - bValue.getTime();
                } else {
                    comparison = aValue > bValue ? 1 : bValue > aValue ? -1 : 0;
                }

                return sortConfig.direction === 'asc' ? comparison : -comparison;
            });
        }
        return sortableItems;
    }, [filteredPeople, sortConfig]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key) {
            direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
        } else if (key === 'persona_fit_score') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const showSkeletons = isLoading && !hydratedFromCache;

    const personIds = useMemo(() => {
        return people.map(p => p.person_id).filter(Boolean);
    }, [people]);

    return (
        <PlaybookStatusProvider personIds={personIds} currentTenant={currentTenant}>
            <div className="bg-gray-50 -m-8 -mt-8 min-h-screen">
                <div className="bg-white border-b mb-6 -mx-8">
                    <div className="max-w-7xl mx-auto px-12 sm:px-8 md:px-10 py-8">
                        <h1 className="text-3xl font-bold text-[#0A1A2F]">{t('people')}</h1>
                        <p className="text-lg text-[#5C6B7A]">{t('keyDecisionMakers')}</p>
                    </div>
                </div>
            <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 py-0">

                <div className="mb-6">
                    <div className="flex flex-col md:flex-row md:flex-wrap gap-3">
                        <div className="relative flex-1 min-w-[260px] md:min-w-[360px] md:max-w-[520px]">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <Input
                                placeholder={t('searchPeople')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 text-sm"
                            />
                        </div>
                        {availableCountries.length > 0 && (
                            <div className="flex items-center gap-2">
                                <Filter className="w-4 h-4 text-[#5C6B7A] hidden md:block" />
                                <Select value={countryFilter} onValueChange={setCountryFilter}>
                                    <SelectTrigger className="w-full md:w-40 text-sm">
                                        <SelectValue placeholder={t('filterByCountry')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('allCountries')}</SelectItem>
                                        {availableCountries.map(country => (
                                            <SelectItem key={country} value={country}>{country}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {availableEvents.length > 0 && (
                            <div className="flex items-center gap-2">
                                <Filter className="w-4 h-4 text-[#5C6B7A] hidden md:block" />
                                <Select value={eventFilter} onValueChange={setEventFilter}>
                                    <SelectTrigger className="w-full md:w-40 text-sm">
                                        <SelectValue placeholder={t('filterByEvent')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('allEvents')}</SelectItem>
                                        {availableEvents.map(event => (
                                            <SelectItem key={event} value={event}>{event}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {availableAccounts.length > 0 && (
                            <PopoverStaysOpen>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-9 text-sm">
                                        {t('account') || 'Account'} {accountFilter.length ? `(${accountFilter.length})` : ''}
                                        <ChevronDown className="w-4 h-4 ml-1" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-64 p-0" align="start">
                                    <div className="flex flex-wrap gap-2 p-2 border-b text-xs">
                                        <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-[#00C2C2] font-medium" onClick={() => setAccountFilter([...availableAccounts])}>Select all</button>
                                        <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium" onClick={() => setAccountFilter([])}>Clear</button>
                                    </div>
                                    <Command>
                                        <CommandInput placeholder={t('searchAccounts') || 'Search accounts...'} />
                                        <CommandList>
                                            <CommandEmpty>No accounts found.</CommandEmpty>
                                            <CommandGroup>
                                                {availableAccounts.map((acc) => {
                                                    const isSelected = accountFilter.includes(acc);
                                                    return (
                                                        <CommandItem
                                                            key={acc}
                                                            value={acc}
                                                            onSelect={() => {
                                                                setAccountFilter(prev =>
                                                                    isSelected ? prev.filter(x => x !== acc) : [...prev, acc]
                                                                );
                                                            }}
                                                        >
                                                            <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary ${isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible'}`}>
                                                                <Check className="h-4 w-4" />
                                                            </div>
                                                            <span className="truncate">{acc}</span>
                                                        </CommandItem>
                                                    );
                                                })}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </PopoverStaysOpen>
                        )}
                        {availableDepartments.length > 0 && (
                            <PopoverStaysOpen>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-9 text-sm">
                                        {t('department')} {departmentFilter.length ? `(${departmentFilter.length})` : ''}
                                        <ChevronDown className="w-4 h-4 ml-1" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-56 max-h-60 overflow-y-auto" align="start">
                                    <div className="flex flex-wrap gap-2 pb-2 border-b text-xs mb-2">
                                        <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-[#00C2C2] font-medium" onClick={() => setDepartmentFilter([...availableDepartments])}>Select all</button>
                                        <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium" onClick={() => setDepartmentFilter([])}>Clear</button>
                                    </div>
                                    {availableDepartments.map((d) => (
                                        <label key={d} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                                            <Checkbox
                                                checked={departmentFilter.includes(d)}
                                                onCheckedChange={(checked) => {
                                                    setDepartmentFilter(prev => checked ? [...prev, d] : prev.filter(x => x !== d));
                                                }}
                                            />
                                            <span className="text-sm truncate">{d}</span>
                                        </label>
                                    ))}
                                </PopoverContent>
                            </PopoverStaysOpen>
                        )}
                        {availableArchetypes.length > 0 && (
                            <PopoverStaysOpen>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-9 text-sm">
                                        {t('archetype')} {archetypeFilter.length ? `(${archetypeFilter.length})` : ''}
                                        <ChevronDown className="w-4 h-4 ml-1" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-56 max-h-60 overflow-y-auto" align="start">
                                    <div className="flex flex-wrap gap-2 pb-2 border-b text-xs mb-2">
                                        <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-[#00C2C2] font-medium" onClick={() => setArchetypeFilter([...availableArchetypes])}>Select all</button>
                                        <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium" onClick={() => setArchetypeFilter([])}>Clear</button>
                                    </div>
                                    {availableArchetypes.map((a) => (
                                        <label key={a} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                                            <Checkbox
                                                checked={archetypeFilter.includes(a)}
                                                onCheckedChange={(checked) => {
                                                    setArchetypeFilter(prev => checked ? [...prev, a] : prev.filter(x => x !== a));
                                                }}
                                            />
                                            <span className="text-sm truncate">{a}</span>
                                        </label>
                                    ))}
                                </PopoverContent>
                            </PopoverStaysOpen>
                        )}
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-[#5C6B7A] hidden md:block" />
                            <span className="text-sm text-[#5C6B7A] whitespace-nowrap">{t('formerClient')}</span>
                            <Select value={formerClientFilter} onValueChange={setFormerClientFilter}>
                                <SelectTrigger className="w-full md:w-48 text-sm">
                                    <SelectValue placeholder={t('all')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('all')}</SelectItem>
                                    <SelectItem value="former_client">{t('formerClientOnly')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-[#5C6B7A] hidden md:block" />
                            <span className="text-sm text-[#5C6B7A] whitespace-nowrap">{t('connections')}</span>
                            <Select value={connectionsFilter} onValueChange={setConnectionsFilter}>
                                <SelectTrigger className="w-full md:w-48 text-sm">
                                    <SelectValue placeholder={t('all')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('all')}</SelectItem>
                                    <SelectItem value="with_connections">{t('withConnections')}</SelectItem>
                                    <SelectItem value="without_connections">{t('withoutConnections')}</SelectItem>
                                    <SelectItem value="alma_mater">{t('almaMater')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-[#5C6B7A] hidden md:block" />
                            <span className="text-sm text-[#5C6B7A] whitespace-nowrap">{t('pursuit')}</span>
                            <Select value={pursuitFilter} onValueChange={setPursuitFilter}>
                                <SelectTrigger className="w-full md:w-48 text-sm">
                                    <SelectValue placeholder={t('all')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('all')}</SelectItem>
                                    <SelectItem value="in_pursuit">{t('inPursuit')}</SelectItem>
                                    <SelectItem value="not_in_pursuit">{t('notInPursuit')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Input
                                type="number"
                                min={0}
                                max={100}
                                placeholder={t('minSocialScore')}
                                value={socialPresenceMinScore}
                                onChange={(e) => setSocialPresenceMinScore(e.target.value)}
                                className="w-40 h-10 text-base min-w-[8rem]"
                            />
                        </div>
                    </div>
                </div>

                <div className="hidden md:block border rounded-lg shadow-md">
                    {showSkeletons ? (
                        <div className="p-6">
                            <Skeleton className="h-64 w-full" />
                        </div>
                    ) : (
                        <div className="relative w-full overflow-x-auto">
                            <Table className="table-fixed">
                                <colgroup><col style={{ width: '19rem' }} /><col style={{ width: '5rem' }} /><col style={{ width: '14rem' }} /><col style={{ width: '10rem' }} /><col style={{ width: '8rem' }} /><col style={{ width: '10rem' }} /><col style={{ width: '8rem' }} /></colgroup>
                                <TableHeader>
                                    <TableRow className="bg-gray-50 hover:bg-gray-50">
                                      <TableHead className="px-4 py-3 text-center cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('first_name')}>
                                        <div className="flex items-center justify-center gap-1">
                                          {t('name')}
                                          {sortConfig.key === 'first_name' && (
                                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>
                                          )}
                                        </div>
                                      </TableHead>
                                      <TableHead className="px-4 py-3 text-center">
                                        <span className="text-xs text-[#5C6B7A]">{t('pursuit')}</span>
                                      </TableHead>
                                      <TableHead className="px-4 py-3 text-center cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('account_name')}>
                                        <div className="flex items-center justify-center gap-1">
                                          {t('account')}
                                          {sortConfig.key === 'account_name' && (
                                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>
                                          )}
                                        </div>
                                      </TableHead>
                                      <TableHead className="px-4 py-3 text-center cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('country')}>
                                        <div className="flex items-center justify-center gap-1">
                                          {t('country')}
                                          {sortConfig.key === 'country' && (
                                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>
                                          )}
                                        </div>
                                      </TableHead>
                                      <TableHead className="px-4 py-3 text-center cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('department')}>
                                        <div className="flex items-center justify-center gap-1">
                                          {t('department')}
                                          {sortConfig.key === 'department' && (
                                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>
                                          )}
                                        </div>
                                      </TableHead>
                                      <TableHead className="px-4 py-3 text-center cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('archetype_name')}>
                                        <div className="flex items-center justify-center gap-1">
                                          {t('archetype')}
                                          {sortConfig.key === 'archetype_name' && (
                                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>
                                          )}
                                        </div>
                                      </TableHead>
                                      <TableHead className="px-4 py-3 text-center cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('persona_fit_score')}>
                                        <div className="flex items-center justify-center gap-1">
                                          {t('fitScore')}
                                          {sortConfig.key === 'persona_fit_score' && (
                                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>
                                          )}
                                        </div>
                                      </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sortedPeople.map((person) => {
                                      const fit = getPersonaLabel(person.persona_fit_score, t);
                                      const personIdForLink = sanitizeId(person.person_id || person.id);
                                      const accountIdForLink = sanitizeId(person._matchedAccountId || person.account_id || '');
                                      const hasConnections = personHasConnections(person);
                                      const almaMaterMatch = almaMaterMatchByPersonId.get(
                                        person.person_id || person.id
                                      );

                                      return (
                                          <TableRow key={person.id || person.person_id} className="hover:bg-gray-50">
                                            <TableCell className="px-4 py-3">
                                              <Link
                                                to={createPageUrl(`PersonProfile?id=${encodeURIComponent(personIdForLink)}`)}
                                                className="flex items-center gap-3 hover:text-[#00C2C2] transition-colors"
                                              >
                                              <PersonAvatar
                                                personId={person.person_id}
                                                hostedPhotoUrl={person.hosted_photo_url}
                                                linkedinPhotoUrl={person.profile_photo_url}
                                                firstName={person.first_name}
                                                lastName={person.last_name}
                                                 size="md"
                                              />
                                              <div className="min-w-0">
                                                <div className="flex items-center gap-1.5">
                                                  <p className="font-medium text-[#0A1A2F] break-words leading-tight">
                                                    {person.first_name} {person.last_name}
                                                  </p>
                                                  {person.linkedin_url && (
                                                    <TooltipProvider delayDuration={0}>
                                                      <Tooltip>
                                                        <TooltipTrigger asChild>
                                                          <a
                                                            href={person.linkedin_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="flex items-center text-[#0077B5] hover:text-[#005885] transition-colors flex-shrink-0"
                                                          >
                                                            <Linkedin className="w-3.5 h-3.5" />
                                                          </a>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                          <p className="text-sm">LinkedIn Profile</p>
                                                        </TooltipContent>
                                                      </Tooltip>
                                                    </TooltipProvider>
                                                  )}
                                                  {person.social_presence_score != null &&
                                                    person.social_presence_score > 0 && (
                                                    <TooltipProvider delayDuration={0}>
                                                      <Tooltip>
                                                        <TooltipTrigger asChild>
                                                          <div className="inline-block cursor-help">
                                                            <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200 text-[10px] px-1.5 py-0.5">
                                                              {person.social_presence_score}
                                                            </Badge>
                                                          </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent 
                                                          side="top" 
                                                          className="max-w-[250px] bg-[#0A1A2F] text-white border-0 shadow-lg p-3 z-[9999]"
                                                        >
                                                          <p className="text-sm font-semibold mb-1 text-[#00C2C2]">Social Presence Score</p>
                                                          <p className="text-sm text-white leading-relaxed">
                                                            An indicator of a persona's overall visibility and activity on LinkedIn. Higher scores suggest they're more likely to respond and engage with sales outreach on the platform.
                                                          </p>
                                                        </TooltipContent>
                                                      </Tooltip>
                                                    </TooltipProvider>
                                                  )}
                                                  <PlaybookMarker personId={person.person_id} />
                                                  {(effectiveTenant === 'learnwise' || effectiveTenant === 'cayuse') &&
                                                    person.product_affinity && (
                                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">
                                                      {person.product_affinity}
                                                    </Badge>
                                                  )}
                                                </div>
                                                <p className="text-xs text-[#5C6B7A] truncate mt-0.5">{person.role_title}</p>
                                                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                                                  {person.worked_for_customer && (
                                                    <TooltipProvider delayDuration={0}>
                                                      <Tooltip>
                                                        <TooltipTrigger asChild>
                                                          <span
                                                            className="inline-flex cursor-help flex-shrink-0"
                                                            onClick={(e) => e.stopPropagation()}
                                                          >
                                                            <Badge className="bg-blue-500 text-white border-0 text-[10px] px-1.5 py-0 leading-tight">
                                                              {t('formerClient')}
                                                            </Badge>
                                                          </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent className="max-w-xs bg-[#0A1A2F] text-white border-0 shadow-lg">
                                                          <p className="text-sm">
                                                            {person.client_name || t('previouslyWorked')}
                                                          </p>
                                                        </TooltipContent>
                                                      </Tooltip>
                                                    </TooltipProvider>
                                                  )}
                                                  {hasConnections && (
                                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">
                                                      {t('connections')}
                                                    </Badge>
                                                  )}
                                                  {almaMaterMatch && (
                                                    <TooltipProvider delayDuration={0}>
                                                      <Tooltip>
                                                        <TooltipTrigger asChild>
                                                          <span
                                                            className="inline-flex flex-shrink-0"
                                                            onClick={(e) => e.stopPropagation()}
                                                          >
                                                            <GraduationCap
                                                              className="w-3.5 h-3.5 text-teal-600"
                                                              aria-label={`Shared alma mater: ${almaMaterMatch.matched_university}`}
                                                            />
                                                          </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                          <p className="text-sm">
                                                            Shared alma mater: {almaMaterMatch.matched_university}
                                                          </p>
                                                        </TooltipContent>
                                                      </Tooltip>
                                                    </TooltipProvider>
                                                  )}
                                                </div>
                                              </div>
                                            </Link>
                                          </TableCell>
                                          <TableCell className="px-4 py-3">
                                            <PursuitMarker isInPursuit={person.add_to_pursuit} />
                                          </TableCell>
                                          <TableCell className="px-4 py-3">
                                            {person._accountMatch && accountIdForLink ? (
                                              <Link 
                                                to={createPageUrl(`AccountFinal?id=${encodeURIComponent(accountIdForLink)}`)}
                                                className="text-[#00C2C2] hover:text-[#00A3A3] hover:underline truncate block"
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                {person.account_name}
                                              </Link>
                                            ) : (
                                              <span className="text-[#5C6B7A] truncate block">{person.account_name || '—'}</span>
                                            )}
                                          </TableCell>
                                          <TableCell className="px-4 py-3 text-center">
                                            <span className="text-sm text-[#5C6B7A]">{person.country || '—'}</span>
                                          </TableCell>
                                          <TableCell className="px-4 py-3 text-center">
                                            {person.department ? (
                                              <Badge variant="outline" className="text-xs">
                                                {person.department}
                                              </Badge>
                                            ) : (
                                              <span className="text-sm text-[#5C6B7A]">—</span>
                                            )}
                                          </TableCell>
                                          <TableCell className="px-4 py-3 text-center">
                                            <span className="text-sm text-[#5C6B7A]">{person.archetype_name || '—'}</span>
                                          </TableCell>
                                          <TableCell className="px-4 py-3 text-center">
                                            {person.persona_score_rationale ? (
                                                <TooltipProvider delayDuration={0}>
                                                  <Tooltip>
                                                    <TooltipTrigger asChild>
                                                      <button 
                                                        className="touch-manipulation inline-flex items-center justify-center"
                                                        onClick={(e) => {
                                                          e.preventDefault();
                                                          e.stopPropagation();
                                                        }}
                                                      >
                                                        <Badge className={`${fit.color} text-xs whitespace-nowrap cursor-help`}>
                                                          {fit.label} ({person.persona_fit_score || 0})
                                                        </Badge>
                                                      </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent 
                                                      side="left" 
                                                      align="center"
                                                      sideOffset={8}
                                                      className="w-[400px] bg-[#0A1A2F] text-white border-0 shadow-lg p-4 z-[9999]"
                                                    >
                                                      <p className="text-sm font-semibold mb-2 text-[#00C2C2]">{t('personaFitRationale')}</p>
                                                      <p className="text-sm text-white leading-relaxed">{person.persona_score_rationale}</p>
                                                    </TooltipContent>
                                                  </Tooltip>
                                                </TooltipProvider>
                                              ) : (
                                                <Badge className={`${fit.color} text-xs whitespace-nowrap`}>
                                                  {fit.label} ({person.persona_fit_score || 0})
                                                </Badge>
                                              )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>

                <div className="md:hidden">
                    {showSkeletons ? (
                        <div className="space-y-3">
                            {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-20 w-full" />)}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {sortedPeople.map((person) => {
                                  const fit = getPersonaLabel(person.persona_fit_score, t);
                                  const personIdForLink = sanitizeId(person.person_id || person.id);
                                  const accountIdForLink = sanitizeId(person._matchedAccountId || person.account_id || '');
                                  const hasConnections = personHasConnections(person);
                                  const almaMaterMatch = almaMaterMatchByPersonId.get(
                                    person.person_id || person.id
                                  );

                                  return (
                                   <Card key={person.id || person.person_id} className="hover:shadow-md transition-shadow">
                                     <CardContent className="p-3">
                                       <Link to={createPageUrl(`PersonProfile?id=${encodeURIComponent(personIdForLink)}`)}>
                                         <div className="flex items-start gap-2 mb-2">
                                         <PersonAvatar
                                                personId={person.person_id}
                                                hostedPhotoUrl={person.hosted_photo_url}
                                                linkedinPhotoUrl={person.profile_photo_url}
                                                firstName={person.first_name}
                                                lastName={person.last_name}
                                                 size="md"
                                          />

                                           <div className="flex-1 min-w-0">
                                             <div className="flex items-start justify-between gap-1 mb-0.5">
                                               <div className="min-w-0">
                                                 <div className="flex items-center gap-1 min-w-0">
                                                   <h3 className="font-semibold text-sm text-[#0A1A2F] break-words leading-tight">
                                                     {person.first_name} {person.last_name}
                                                   </h3>
                                                   {person.linkedin_url && (
                                                     <TooltipProvider delayDuration={0}>
                                                       <Tooltip>
                                                         <TooltipTrigger asChild>
                                                           <a
                                                             href={person.linkedin_url}
                                                             target="_blank"
                                                             rel="noopener noreferrer"
                                                             onClick={(e) => e.stopPropagation()}
                                                             className="flex items-center text-[#0077B5] hover:text-[#005885] transition-colors flex-shrink-0"
                                                           >
                                                             <Linkedin className="w-3.5 h-3.5" />
                                                           </a>
                                                         </TooltipTrigger>
                                                         <TooltipContent>
                                                           <p className="text-sm">LinkedIn Profile</p>
                                                         </TooltipContent>
                                                       </Tooltip>
                                                     </TooltipProvider>
                                                   )}
                                                   {person.social_presence_score != null &&
                                                     person.social_presence_score > 0 && (
                                                     <TooltipProvider delayDuration={0}>
                                                       <Tooltip>
                                                         <TooltipTrigger asChild>
                                                           <div className="inline-block cursor-help flex-shrink-0">
                                                             <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200 text-[10px] px-1.5 py-0.5">
                                                               {person.social_presence_score}
                                                             </Badge>
                                                           </div>
                                                         </TooltipTrigger>
                                                         <TooltipContent 
                                                           side="top" 
                                                           className="max-w-[250px] bg-[#0A1A2F] text-white border-0 shadow-lg p-3 z-[9999]"
                                                         >
                                                           <p className="text-sm font-semibold mb-1 text-[#00C2C2]">Social Presence Score</p>
                                                           <p className="text-sm text-white leading-relaxed">
                                                             An indicator of a persona's overall visibility and activity on LinkedIn. Higher scores suggest they're more likely to respond and engage with sales outreach on the platform.
                                                           </p>
                                                         </TooltipContent>
                                                       </Tooltip>
                                                     </TooltipProvider>
                                                   )}
                                                   <PlaybookMarker personId={person.person_id} />
                                                   {(effectiveTenant === 'learnwise' || effectiveTenant === 'cayuse') &&
                                                     person.product_affinity && (
                                                     <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">
                                                       {person.product_affinity}
                                                     </Badge>
                                                   )}
                                                 </div>
                                                 <p className="text-xs text-[#5C6B7A] truncate leading-tight mt-0.5">{person.role_title}</p>
                                                 <div className="flex flex-wrap items-center gap-1 mt-0.5">
                                                   <PursuitMarker isInPursuit={person.add_to_pursuit} />
                                                   {person.worked_for_customer && (
                                                     <TooltipProvider delayDuration={0}>
                                                       <Tooltip>
                                                         <TooltipTrigger asChild>
                                                           <span
                                                             className="inline-flex cursor-help flex-shrink-0"
                                                             onClick={(e) => e.stopPropagation()}
                                                           >
                                                             <Badge className="bg-blue-500 text-white border-0 text-[10px] px-1.5 py-0 leading-tight">
                                                               {t('formerClient')}
                                                             </Badge>
                                                           </span>
                                                         </TooltipTrigger>
                                                         <TooltipContent className="max-w-xs bg-[#0A1A2F] text-white border-0 shadow-lg">
                                                           <p className="text-sm">
                                                             {person.client_name || t('previouslyWorked')}
                                                           </p>
                                                         </TooltipContent>
                                                       </Tooltip>
                                                     </TooltipProvider>
                                                   )}
                                                   {hasConnections && (
                                                     <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">
                                                       {t('connections')}
                                                     </Badge>
                                                   )}
                                                   {almaMaterMatch && (
                                                     <TooltipProvider delayDuration={0}>
                                                       <Tooltip>
                                                         <TooltipTrigger asChild>
                                                           <span
                                                             className="inline-flex flex-shrink-0"
                                                             onClick={(e) => e.stopPropagation()}
                                                           >
                                                             <GraduationCap
                                                               className="w-3.5 h-3.5 text-teal-600"
                                                               aria-label={`Shared alma mater: ${almaMaterMatch.matched_university}`}
                                                             />
                                                           </span>
                                                         </TooltipTrigger>
                                                         <TooltipContent>
                                                           <p className="text-sm">
                                                             Shared alma mater: {almaMaterMatch.matched_university}
                                                           </p>
                                                         </TooltipContent>
                                                       </Tooltip>
                                                     </TooltipProvider>
                                                   )}
                                                 </div>
                                               </div>
                                               <div className="flex items-center gap-1 flex-shrink-0">
                                                 {person.persona_score_rationale ? (
                                                     <TooltipProvider delayDuration={0}>
                                                       <Tooltip>
                                                         <TooltipTrigger asChild>
                                                           <button 
                                                             className="touch-manipulation"
                                                             onClick={(e) => {
                                                               e.preventDefault();
                                                               e.stopPropagation();
                                                             }}
                                                           >
                                                             <Badge className={`${fit.color} text-[10px] px-1.5 py-0.5 cursor-help`}>
                                                               {person.persona_fit_score || 0}
                                                             </Badge>
                                                           </button>
                                                         </TooltipTrigger>
                                                         <TooltipContent 
                                                           side="bottom" 
                                                           align="end"
                                                           className="w-[90vw] max-w-[400px] bg-[#0A1A2F] text-white border-0 shadow-lg p-4 z-[9999]"
                                                         >
                                                           <p className="text-sm font-semibold mb-2 text-[#00C2C2]">{t('personaFitRationale')}</p>
                                                           <p className="text-sm text-white leading-relaxed">{person.persona_score_rationale}</p>
                                                         </TooltipContent>
                                                       </Tooltip>
                                                     </TooltipProvider>
                                                   ) : (
                                                     <Badge className={`${fit.color} text-[10px] px-1.5 py-0.5`}>
                                                       {person.persona_fit_score || 0}
                                                     </Badge>
                                                   )}
                                               </div>
                                             </div>
                                           </div>
                                         </div>
                                       </Link>
                                       <div className="space-y-1">
                                         <div className="flex items-center justify-between text-xs">
                                           <span className="text-[#5C6B7A]">{t('account')}:</span>
                                           {person._accountMatch && accountIdForLink ? (
                                             <Link 
                                               to={createPageUrl(`AccountFinal?id=${encodeURIComponent(accountIdForLink)}`)}
                                               className="text-[#00C2C2] hover:text-[#00A3A3] hover:underline truncate max-w-[60%]"
                                               onClick={(e) => e.stopPropagation()}
                                             >
                                               {person.account_name}
                                             </Link>
                                           ) : (
                                             <span className="text-[#5C6B7A] truncate max-w-[60%]">{person.account_name || '—'}</span>
                                           )}
                                         </div>
                                         {person.department && (
                                           <div className="flex items-center justify-between text-xs">
                                             <span className="text-[#5C6B7A]">{t('department')}:</span>
                                             <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">
                                               {person.department}
                                             </Badge>
                                           </div>
                                         )}
                                         {(effectiveTenant === 'learnwise' || effectiveTenant === 'cayuse') &&
                                           person.product_affinity && (
                                           <div className="flex items-center justify-between text-xs">
                                             <span className="text-[#5C6B7A]">Product:</span>
                                             <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">
                                               {person.product_affinity}
                                             </Badge>
                                           </div>
                                         )}
                                         <div className="flex items-center justify-between text-xs">
                                           <span className="text-[#5C6B7A]">{t('archetype')}:</span>
                                           <span className="text-[#5C6B7A] truncate max-w-[60%]">{person.archetype_name || '—'}</span>
                                         </div>
                                       </div>
                                     </CardContent>
                                   </Card>
                                  );
                                  })}
                                  </div>
                                  )}
                                  </div>

                {!isLoading && filteredPeople.length === 0 && (
                    <div className="text-center py-20">
                        <UserIcon className="w-12 h-12 mx-auto text-gray-300" />
                        <h2 className="mt-4 text-xl font-semibold text-[#0A1A2F]">{t('noPeopleFound')}</h2>
                        <p className="text-[#5C6B7A]">{t('adjustFilters')}</p>
                    </div>
                )}
            </div>
            </div>
        </PlaybookStatusProvider>
    );
}